import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { BoxWrapper, Button } from 'components/Base';
import Tables from 'components/Table';
import Store from 'store/store';
import { PolicyType } from 'store/modules/Policies/types';
import api from 'api';
import { CircularProgress } from '@material-ui/core';
import Policy from './Policy';
import Router from 'components/Router/Router';
import { errHelper } from 'utils';

const { connect, selectors, actions } = Store;
const { Table, TableBody, TableHead } = Tables;
const { useParams } = Router;

const useStyles = makeStyles({
  btnContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '2rem'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem',
    minWidth: '60rem'
  },
  loaderWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  }
});

interface Props {
  closeModal: () => void;
  recommendedPolicies: Array<PolicyType>;
  getRecommendationsData: any;
}

const SellectionOvveride = ({
  closeModal,
  recommendedPolicies,
  getRecommendationsData
}: Props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [providers, setProviders] = useState<any>([]);
  const [chargeOverrides, setChargeOverrides] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState({} as any);
  const [isApplyFixedChargeVisible, setIsApplyFixedChargeVisible] =
    useState(true);
  const [tableHeaders, setTableHeaders] = useState([] as any);
  const classes = useStyles();
  const { portfolio } = useParams() as any;

  const determineTableHeaders = () => {
    const newHeaders = [
      'Policy owner(s)',
      'Policy number',
      'Policy type',
      'Policy provider',
      'Policy platform',
      'Override',
      'Discount'
    ];

    if (isApplyFixedChargeVisible) {
      newHeaders.splice(5, 0, 'Apply fixed charge');
    }

    setTableHeaders(newHeaders);
  };

  const evaluate = () => {
    setIsProcessing(true);
    Promise.all(
      chargeOverrides.map((co: any) =>
        api.providerPlatformChargesApi.discountCharges(co, portfolio, co.policy)
      )
    ).then(() => {
      api
        .evaluate({ portfolio })
        .then(() => {
          getRecommendationsData({ portfolio }).then(() => {
            setIsProcessing(false);
            closeModal();
          });
        })
        .catch((err: any) => {
          setIsProcessing(false);
          errHelper(err);
        });
    });
  };

  useEffect(() => {
    determineTableHeaders();
    setIsLoading(true);
    api
      .getCipProviders()
      .then(({ data }: any) => {
        setProviders(data);
        setIsLoading(false);
      })
      .catch((err: any) => errHelper(err));
  }, []);

  useEffect(() => {
    setIsApplyFixedChargeVisible(
      recommendedPolicies.some((policy: any) =>
        providers.some(
          (provider: any) =>
            provider.policy_type === policy.type &&
            provider.providers.some(
              (nestedProvider: any) =>
                nestedProvider.policy_platform === policy.platform &&
                nestedProvider.policy_provider === policy.provider &&
                nestedProvider.applicable_to_single_policy
            )
        )
      )
    );
  }, [providers, recommendedPolicies]);

  useEffect(() => {
    determineTableHeaders();
  }, [isApplyFixedChargeVisible]);

  useEffect(() => {
    setSelectedPolicy(
      (
        recommendedPolicies.find(
          (policy: PolicyType) => policy.apply_fixed_charge
        ) ||
        recommendedPolicies.find((policy: PolicyType) => policy.type === 9) ||
        recommendedPolicies[0]
      )?.id
    );
  }, [recommendedPolicies]);

  const updateSelectedPolicy = (id: number) => {
    api.setChargeablePolicy(portfolio, id);
  };

  return (
    <BoxWrapper
      title="Selection override"
      headerChildren={
        <div className={classes.loaderWrapper}>
          {isLoading && <CircularProgress size="1.5rem" />}
        </div>
      }
      closeModal={closeModal}
    >
      <div className={classes.container}>
        <Table>
          <TableHead cells={tableHeaders} />
          <TableBody>
            {!isLoading &&
              recommendedPolicies.map((policy: PolicyType, index: number) => {
                const optionsToOverride = providers.find((provider: any) => {
                  return provider.policy_type === policy.type;
                });
                const providersOverride =
                  (optionsToOverride && optionsToOverride.providers) || [];

                const options =
                  providersOverride &&
                  providersOverride
                    .map((provider: any) => ({
                      value: provider.id,
                      name: provider.name
                    }))
                    .filter(
                      (item: any, index: number, self: any) =>
                        index ===
                        self.findIndex((t: any) => t.name === item.name)
                    );

                return (
                  <Policy
                    form={`policy-${index}`}
                    initialValues={{
                      policy: policy,
                      options: options || []
                    }}
                    key={policy.id}
                    chargeOverrides={chargeOverrides}
                    setChargeOverrides={setChargeOverrides}
                    selectedPolicy={selectedPolicy}
                    setSelectedPolicy={(id: number) => {
                      setSelectedPolicy(id);
                      updateSelectedPolicy(id);
                    }}
                    index={index}
                    isApplyFixedChargeVisible={isApplyFixedChargeVisible}
                  />
                );
              })}
          </TableBody>
        </Table>
        <div className={classes.btnContainer}>
          <Button color="primary" onClick={closeModal}>
            Cancel
          </Button>
          <Button isLoading={isProcessing} type="submit" onClick={evaluate}>
            Evaluate
          </Button>
        </div>
      </div>
    </BoxWrapper>
  );
};

export default connect(
  (state: any) => ({
    recommendedPolicies: selectors.caseSelectors.policies(state)
  }),
  {
    getRecommendationsData: actions.recommendationActions.getRecommendationData
  }
)(SellectionOvveride);
